import React, { useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import { FiLock, FiMessageSquare, FiAlertCircle } from "react-icons/fi";
import useAxios from "../../hooks/useAxios";
import swal from "sweetalert";

const RATING_LABELS = {
    1: { text: "Muy insatisfecho", color: "#ef4444" },
    2: { text: "Insatisfecho", color: "#f97316" },
    3: { text: "Regular / Neutral", color: "#eab308" },
    4: { text: "Satisfecho", color: "#3b82f6" },
    5: { text: "Muy satisfecho", color: "#10b981" },
};

const ClosePlanSurveyModal = ({
    show,
    onHide,
    planId,
    costCenterName = "",
    onClosed,
    apiEndpoint = null, // defaults to `/plans/${planId}/toggle-status`
}) => {
    const [score, setScore] = useState(0);
    const [hoverScore, setHoverScore] = useState(0);
    const [comment, setComment] = useState("");
    const [touched, setTouched] = useState(false);

    const endpoint = apiEndpoint || `/plans/${planId}/toggle-status`;

    const [{ loading: submitting }, submitClosePlan] = useAxios(
        { url: endpoint, method: "PUT" },
        { manual: true, useCache: false }
    );

    const handleStarClick = (rating) => {
        setScore(rating);
    };

    const handleClose = () => {
        if (!submitting) {
            setScore(0);
            setHoverScore(0);
            setComment("");
            setTouched(false);
            onHide?.();
        }
    };

    const isScoreValid = score >= 1 && score <= 5;
    const isCommentValid = comment.trim().length >= 3;
    const canSubmit = isScoreValid && isCommentValid && !submitting;

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        setTouched(true);

        if (!isScoreValid || !isCommentValid) {
            swal({
                title: "Campos incompletos",
                text: "Para cerrar el plan es obligatorio calificar del 1 al 5 y escribir un comentario.",
                icon: "warning",
            });
            return;
        }

        try {
            await submitClosePlan({
                data: {
                    score: Number(score),
                    comment: comment.trim(),
                },
            });

            swal({
                title: "¡Plan Cerrado!",
                text: "La encuesta de satisfacción fue registrada y el plan se ha cerrado correctamente.",
                icon: "success",
                timer: 2500,
                buttons: false,
            });

            handleClose();
            onClosed?.();
        } catch (error) {
            const errorMsg =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.score?.[0] ||
                error?.response?.data?.errors?.comment?.[0] ||
                "Ocurrió un error al cerrar el plan.";

            swal({
                title: "Error al cerrar el plan",
                text: errorMsg,
                icon: "error",
            });
        }
    };

    const activeScore = hoverScore || score;

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false}>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!submitting} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <Modal.Title
                        className="d-flex align-items-center gap-2"
                        style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" }}
                    >
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: "8px",
                                background: "#fef2f2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#ef4444",
                                marginRight: "8px",
                            }}
                        >
                            <FiLock size={18} />
                        </div>
                        <div>
                            <div>Cierre de Planificación</div>
                            <small
                                className="text-muted fw-normal"
                                style={{ fontSize: "0.75rem", display: "block" }}
                            >
                                Encuesta de satisfacción obligatoria
                            </small>
                        </div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: "1.25rem" }}>
                    <Alert variant="warning" className="d-flex align-items-start gap-2 py-2 px-3 mb-3">
                        <FiAlertCircle className="mt-1 flex-shrink-0" size={16} />
                        <div style={{ fontSize: "0.82rem", lineHeight: "1.35" }}>
                            {costCenterName ? (
                                <>
                                    Estás por cerrar la planificación de <strong>{costCenterName}</strong>.
                                </>
                            ) : (
                                "Estás por cerrar esta planificación."
                            )}
                            <br />
                            <strong>Es obligatorio responder la encuesta</strong> (puntuación del 1 al 5 y
                            comentario) para poder completar el cierre.
                        </div>
                    </Alert>

                    {/* Calificación de Estrellas */}
                    <Form.Group className="mb-3 text-center">
                        <Form.Label
                            className="fw-semibold d-block mb-1"
                            style={{ fontSize: "0.88rem", color: "#334155" }}
                        >
                            ¿Qué tan satisfecho(a) estás con este proceso de planificación?{" "}
                            <span className="text-danger">*</span>
                        </Form.Label>

                        <div
                            className="d-flex justify-content-center align-items-center gap-2 my-2"
                            onMouseLeave={() => setHoverScore(0)}
                            style={{ cursor: "pointer" }}
                        >
                            {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                    key={star}
                                    onMouseEnter={() => setHoverScore(star)}
                                    onClick={() => handleStarClick(star)}
                                    style={{
                                        fontSize: "2.4rem",
                                        transition: "transform 0.15s ease, color 0.15s ease",
                                        transform: (hoverScore === star || score === star) ? "scale(1.2)" : "scale(1)",
                                        color: star <= activeScore ? "#f59e0b" : "#cbd5e1",
                                        userSelect: "none",
                                        padding: "0 2px",
                                    }}
                                    title={`${star} de 5`}
                                >
                                    {star <= activeScore ? <AiFillStar /> : <AiOutlineStar />}
                                </span>
                            ))}
                        </div>

                        {/* Etiqueta descriptiva del puntaje */}
                        <div style={{ minHeight: "24px" }}>
                            {activeScore > 0 ? (
                                <span
                                    className="badge px-3 py-1"
                                    style={{
                                        fontSize: "0.8rem",
                                        backgroundColor: RATING_LABELS[activeScore]?.color,
                                        color: "#ffffff",
                                        fontWeight: 600,
                                    }}
                                >
                                    {activeScore} / 5 — {RATING_LABELS[activeScore]?.text}
                                </span>
                            ) : (
                                <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                                    Selecciona una calificación del 1 al 5 estrellas
                                </span>
                            )}
                        </div>

                        {touched && !isScoreValid && (
                            <div className="text-danger mt-1" style={{ fontSize: "0.78rem" }}>
                                Debes seleccionar una puntuación obligatoria.
                            </div>
                        )}
                    </Form.Group>

                    {/* Comentario / Observaciones */}
                    <Form.Group className="mb-2">
                        <Form.Label
                            className="fw-semibold d-flex justify-content-between align-items-center mb-1"
                            style={{ fontSize: "0.88rem", color: "#334155" }}
                        >
                            <span>
                                <FiMessageSquare className="me-1" />
                                Comentario u observaciones <span className="text-danger">*</span>
                            </span>
                            <small
                                className={comment.trim().length >= 3 ? "text-success" : "text-muted"}
                                style={{ fontSize: "0.75rem" }}
                            >
                                {comment.trim().length} / mín. 3 caracteres
                            </small>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            placeholder="Déjanos tus comentarios, observaciones o aspectos a mejorar en el proceso de planificación..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            disabled={submitting}
                            isInvalid={touched && !isCommentValid}
                            style={{
                                fontSize: "0.9rem",
                                borderRadius: "8px",
                                resize: "vertical",
                                minHeight: "140px",
                                lineHeight: "1.45",
                                padding: "0.65rem 0.85rem",
                            }}
                        />
                        {touched && !isCommentValid && (
                            <div className="text-danger mt-1" style={{ fontSize: "0.78rem" }}>
                                El comentario es obligatorio para poder cerrar el plan (mínimo 3 caracteres).
                            </div>
                        )}
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer
                    style={{
                        borderTop: "1px solid #e2e8f0",
                        padding: "0.75rem 1.25rem",
                        background: "#f8fafc",
                    }}
                >
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleClose}
                        disabled={submitting}
                        style={{ fontWeight: 500 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        type="submit"
                        disabled={!canSubmit}
                        className="d-flex align-items-center gap-1"
                        style={{ fontWeight: 600 }}
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" />
                                <span>Cerrando plan...</span>
                            </>
                        ) : (
                            <>
                                <FiLock size={14} className="me-1" />
                                <span>Completar encuesta y cerrar plan</span>
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ClosePlanSurveyModal;
